import React, {useCallback, useRef, useState} from "react";
import {Button} from "../../HTMLEntities/Button";
import {setIsLoading} from "../../Layout/actions";
import {useDispatch, useSelector} from "react-redux";
import {AssignedReviewee, WriteACounselorReviewTrackingData} from "../types";
import {addReferralTitleAndUrl, createLeadAndResponse} from "../response";
import {useHistory} from "react-router";
import {WRITE_A_COUNSELOR_REVIEW} from "../../../types";
import {RootStore} from "../../../configureStore";
import {trackClickEvent} from "../../../utils/utils";
import {getRequest} from "../../../utils/request";
import ApiConfig from "../../../config/apiConfig";
import Popup from "../../PopUp/Loadable";
import {AlreadyReviewedText} from "./widgets/Styled";

interface Props {
    trackingData: WriteACounselorReviewTrackingData;
    renderType?: "White Button" | "Orange Button";
}

const reviewedPopupContentStyle: Record<string, unknown> = {
    background: "#fff",
    top: "50%",
    left: "50%",
    right: "",
    bottom: "",
    transform: "translate(-50%,-50%)",
    position: "absolute",
    padding: "0",
    zIndex: "10",
    height: "auto",
    outline: "none",
    width: "340px",
    maxWidth: "90%",
    WebkitTapHighlightColor: "transparent",
};

const reviewedPopupOverlayStyle: Record<string, unknown> = {
    background: "rgba(0, 0, 0, 0.6)",
    position: "fixed",
    top: "0",
    left: "0",
    right: "0",
    bottom: "0",
    zIndex: "9",
    WebkitTapHighlightColor: "transparent",
    overflow: "auto",
};

const WriteACounselorReview: React.FC<Props> = ({
    trackingData: writeAReviewTrackingData,
    renderType = "Orange Button",
}: Props) => {
    const isMobile = useSelector((state: RootStore) => state.layout.isMobile);
    const enableCTA = useRef(true);
    const dispatch = useDispatch();
    const history = useHistory();
    const [isReviewedPopUpOpened, setIsReviewedPopUpOpened] = useState(false);
    const [reviewedCounsellorName, setReviewedCounsellorName] = useState("");

    const closeReviewedPopUp = useCallback(() => {
        setIsReviewedPopUpOpened(false);
    }, []);

    const handleGuideCTA = useCallback(async () => {
        if (!enableCTA.current) {
            return;
        }
        const timer = setTimeout(function () {
            dispatch(setIsLoading(true));
        }, 1000);
        enableCTA.current = false;
        // GA tracking
        trackClickEvent(writeAReviewTrackingData.eventCategory, writeAReviewTrackingData.widget);

        const counselorId = writeAReviewTrackingData.counselorId;
        if (counselorId) {
            const response = (await getRequest(
                `${ApiConfig.GET_ASSIGNED_COUNSELLOR_FOR_REVIEW}?counsellorId=${counselorId}`,
            )) as {data: {data: AssignedReviewee[]}};
            const reviewees = Array.isArray(response?.data?.data) ? response.data.data : [];
            const pending = reviewees.filter((reviewee) => !reviewee.alreadyReviewed);
            if (reviewees.length > 0 && pending.length === 0) {
                const assignedCounsellor = reviewees.find(
                    (reviewee) => Number(reviewee.counsellorId) === Number(counselorId),
                );
                setReviewedCounsellorName(assignedCounsellor?.counsellorName ?? "");
                setIsReviewedPopUpOpened(true);
                clearTimeout(timer);
                dispatch(setIsLoading(false));
                // eslint-disable-next-line require-atomic-updates
                enableCTA.current = true;
                return;
            }
        }

        const trackingData: WriteACounselorReviewTrackingData = {
            ...writeAReviewTrackingData,
            ...addReferralTitleAndUrl(""),
            ctaType: WRITE_A_COUNSELOR_REVIEW as typeof WRITE_A_COUNSELOR_REVIEW,
            isTriggeredFromPWA: true,
        };
        await createLeadAndResponse({
            trackingData,
            isMobile,
            history,
            dispatch,
        });
        clearTimeout(timer);
        dispatch(setIsLoading(false));
        // eslint-disable-next-line require-atomic-updates
        enableCTA.current = true;
    }, [dispatch, history, isMobile, writeAReviewTrackingData]);

    return (
        <>
            <Button onClick={handleGuideCTA} orange={renderType === "Orange Button"}>
                Write a Review
            </Button>
            {isReviewedPopUpOpened && (
                <Popup
                    popUpName="counselorReviewAlreadyPosted"
                    isOpen={true}
                    closeHandle={closeReviewedPopUp}
                    style={{
                        content: reviewedPopupContentStyle,
                        overlay: reviewedPopupOverlayStyle,
                    }}
                    title="Write a Review"
                    shouldCloseOnOverlayClick={true}
                    crossEnabledForMobile={true}
                    showHeading={false}
                >
                    <AlreadyReviewedText>
                        <p>
                            {reviewedCounsellorName
                                ? `A review is already posted for ${reviewedCounsellorName}.`
                                : "A review is already posted for this counsellor."}
                        </p>
                        <Button
                            orange
                            onClick={closeReviewedPopUp}
                            buttonCSS={{
                                mobile: {width: "100px"},
                                laptop: {width: "100px"},
                            }}
                        >
                            OK
                        </Button>
                    </AlreadyReviewedText>
                </Popup>
            )}
        </>
    );
};
export default WriteACounselorReview;
